import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { FollowButton } from '@sorare/core/src/components/favorites/FollowButton';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { DestroyWatchlistButton } from '../DestroyWatchlistButton';
import { DumbWatchlistHeader } from '../DumbWatchlistHeader';
import { EditWatchlistButton } from '../EditWatchlistButton';
import { LazyAddWatchlistToCartButton } from '../LazyAddWatchlistToCartButton';
import { ShareWatchlistButton } from '../ShareWatchlistButton';
import { WatchlistMetas } from '../WatchlistMetas';
import type { WatchlistHeader_watchlist } from './__generated__/index.graphql';

const EndVertical = styled(Vertical)`
  align-self: flex-end;
`;

type Props = {
  watchlist: WatchlistHeader_watchlist;
};

export const WatchlistHeader = ({ watchlist }: Props) => {
  const { commonPlayers, totalPlayersCount } = watchlist;
  const { isMe } = useCurrentUserContext();
  const isMine = isMe(watchlist.user);
  const anyPlayers = commonPlayers?.nodes;
  const isEmpty = totalPlayersCount === 0;
  const isPublic = watchlist.public;
  return (
    <DumbWatchlistHeader
      title={watchlist.title}
      playerUrls={anyPlayers.map(p => p.anyPlayer.fullPictureUrl)}
      actions={
        <>
          {!isEmpty && !!isMine && (
            <LazyAddWatchlistToCartButton
              watchlistId={watchlist.id}
              size="medium"
              color="quaternary"
              blur
            />
          )}
          {!isMine && (
            <FollowButton
              subscribable={watchlist}
              blur
              currentUserSubscription={watchlist.currentUserSubscription}
            />
          )}

          <Dropdown
            gap={8}
            align="right"
            transparent
            forceDesktopBehavior
            label={p => (
              <IconButton
                {...p}
                icon={faEllipsis}
                size="medium"
                color="quaternary"
                blur
              />
            )}
          >
            {({ closeDropdown }) => (
              <EndVertical>
                {isMine ? (
                  <>
                    <EditWatchlistButton
                      size="medium"
                      watchlist={watchlist}
                      color="quaternary"
                      blur
                    />
                    {!!isPublic && (
                      <ShareWatchlistButton
                        watchlist={watchlist}
                        size="medium"
                        color="quaternary"
                        blur
                        onClick={closeDropdown}
                      />
                    )}
                    <DestroyWatchlistButton
                      watchlist={watchlist}
                      onClose={closeDropdown}
                      color="red"
                      size="medium"
                      blur
                    />
                  </>
                ) : (
                  <>
                    {!isEmpty && (
                      <LazyAddWatchlistToCartButton
                        watchlistId={watchlist.id}
                        size="medium"
                        color="quaternary"
                        blur
                      />
                    )}
                    <ShareWatchlistButton
                      watchlist={watchlist}
                      size="medium"
                      color="quaternary"
                      blur
                      onClick={closeDropdown}
                    />
                  </>
                )}
              </EndVertical>
            )}
          </Dropdown>
        </>
      }
      metas={
        <WatchlistMetas
          watchlist={watchlist}
          withCreatedAt
          hideOwner={isMine}
        />
      }
    />
  );
};

WatchlistHeader.fragments = {
  watchlist: gql`
    fragment WatchlistHeader_watchlist on Watchlist {
      slug
      title
      public
      user {
        slug
      }
      currentUserSubscription {
        slug
      }
      totalPlayersCount
      commonPlayers(first: 7) {
        nodes {
          id
          anyPlayer {
            slug
            fullPictureUrl
          }
        }
      }
      ...WatchlistMetas_watchlist
      ...EditWatchlistButton_watchlist
      ...DestroyWatchlistButton_watchlist
    }
    ${WatchlistMetas.fragments.watchlist}
    ${EditWatchlistButton.fragments.watchlist}
    ${DestroyWatchlistButton.fragments.watchlist}
  ` as TypedDocumentNode<WatchlistHeader_watchlist>,
};
